import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import img from "../assets/profile.png";
import { getProfile, updateProfile, updateAddress } from '../api/api.js';
import eventBus from '../eventBus';
import api from '../Api';

const UserProfile = ({ openReviewModal }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [addressId, setAddressId] = useState(null);
  const [showAllFeedback, setShowAllFeedback] = useState(false);
  const [showAllTechnicians, setShowAllTechnicians] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    building_number: '',
    block: '',
    land_mark: '',
    city: '',
    pincode: '',
    profileImage: img,
    about: ''
  });

  const [apiError, setApiError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [feedbackNotifications, setFeedbackNotifications] = useState([]);
  const [loadingFeedbackNotifications, setLoadingFeedbackNotifications] = useState(true);
  const [feedbackNotificationsError, setFeedbackNotificationsError] = useState('');

  // ----- Profile fetch -----
  useEffect(() => {
    const token = localStorage.getItem('user_token');
    const userInfo = JSON.parse(localStorage.getItem('user_info'));
    if (!token) return;
    (async () => {
      try {
        const resp = await getProfile(token);
        const user = resp.data?.data || resp.data || userInfo || null;
        if (resp.success && user) {
          setUserData(user);
          if (user.addresses && user.addresses.length > 0) {
            setAddressId(user.addresses[0]._id);
          }
          setFormData(prev => ({
            ...prev,
            name: user.name || user.user_name || '',
            email: user.email || '',
            mobile: user.mobile || '',
            profileImage: user.profile_image ? user.profile_image : img,
            building_number: user.addresses?.[0]?.building_number || '',
            block: user.addresses?.[0]?.block || '',
            land_mark: user.addresses?.[0]?.land_mark || '',
            city: user.addresses?.[0]?.city || '',
            pincode: user.addresses?.[0]?.pincode || ''
          }));
        }
      } catch (err) {
        setApiError(err.message || 'Failed to load profile');
        setTimeout(() => setApiError(''), 3000);
      }
    })();
  }, []);

  // ----- Feedback notifications fetch -----
  useEffect(() => {
    const fetchFeedbackNotifications = async () => {
      const token = localStorage.getItem('user_token') || localStorage.getItem('auth_token');
      if (!token) {
        setFeedbackNotifications([]);
        setLoadingFeedbackNotifications(false);
        return;
      }
      try {
        setFeedbackNotificationsError('');
        setLoadingFeedbackNotifications(true);
        const response = await api.get('/api/web/user/feedback-notifications?page=1&limit=50', {
          headers: { accept: '*/*', Authorization: `Bearer ${token}` }
        });
        const data = response.data;
        if (!data?.success || !Array.isArray(data.data)) {
          throw new Error(data?.message || 'Unable to load feedback notifications.');
        }
        setFeedbackNotifications(data.data);
      } catch (error) {
        setFeedbackNotificationsError(error.message || 'Unable to load feedback notifications.');
      } finally {
        setLoadingFeedbackNotifications(false);
      }
    };

    fetchFeedbackNotifications();
    eventBus.on('feedbackUpdated', fetchFeedbackNotifications);
    return () => eventBus.off('feedbackUpdated', fetchFeedbackNotifications);
  }, []);

  // ----- Derived data -----

  // Helper: dedupe by technicianId + userId (unique pair)
  const dedupeByTechUserPair = (items) => {
    const map = new Map();
    for (const n of items) {
      const techId = n.metadata?.technicianId || n.technician?.technician_id || n._id;
      const userId = n.userId || 'unknown';
      const key = `${userId}::${techId}`;
      const existing = map.get(key);
      // Keep the latest one (or the one with submitted status if mixed)
      if (!existing) {
        map.set(key, n);
      } else {
        // Prefer submitted over pending; otherwise latest
        const existingSubmitted = existing.feedback_status === 'submitted';
        const currentSubmitted = n.feedback_status === 'submitted';
        if (currentSubmitted && !existingSubmitted) {
          map.set(key, n);
        } else if (currentSubmitted === existingSubmitted) {
          if (new Date(n.createdAt) > new Date(existing.createdAt)) {
            map.set(key, n);
          }
        }
      }
    }
    return Array.from(map.values());
  };

  // Pending feedbacks - unique by technicianId + userId
  const pendingNotifications = useMemo(() => {
    const pending = feedbackNotifications.filter(n => n.feedback_status === 'pending');
    // Sort by createdAt desc so latest wins in dedupe
    const sorted = [...pending].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    return dedupeByTechUserPair(sorted);
  }, [feedbackNotifications]);

  // Submitted feedbacks - unique by technicianId + userId
  const submittedNotifications = useMemo(() => {
    const submitted = feedbackNotifications.filter(n => n.feedback_status === 'submitted');
    const sorted = [...submitted].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    return dedupeByTechUserPair(sorted);
  }, [feedbackNotifications]);

  // Count of unique submitted pairs (for "Reviews Given" stat)
  const submittedFeedbackCount = submittedNotifications.length;

  // Recently viewed technicians - unique by technician id
  const recentlyViewed = useMemo(() => {
    const rawList = userData?.recently_viewed_technicians?.recently_viewed_technicians || [];
    const map = new Map();
    for (const tech of rawList) {
      const id = tech._id || tech.technician_id;
      if (!map.has(id)) {
        map.set(id, tech);
      }
    }
    return Array.from(map.values());
  }, [userData]);

  const totalViewed =
    userData?.recently_viewed_technicians?.total_viewed_technicians || recentlyViewed.length;

  // Display slices
  const displayedNotifications = showAllFeedback
    ? pendingNotifications
    : pendingNotifications.slice(0, 3);

  const displayedTechnicians = showAllTechnicians
    ? recentlyViewed
    : recentlyViewed.slice(0, 3);

  // ----- Edit / save -----
  const handleEditToggle = () => {
    if (isEditing) {
      const token = localStorage.getItem('user_token');
      const profileFormData = new FormData();
      profileFormData.append('name', formData.name);
      profileFormData.append('email', formData.email);
      profileFormData.append('mobile', formData.mobile);
      if (profileImageFile) profileFormData.append('profile_pic', profileImageFile);

      const addressPayload = {
        building_number: formData.building_number,
        block: formData.block,
        land_mark: formData.land_mark,
        city: formData.city,
        pincode: Number(formData.pincode) || 0,
      };

      (async () => {
        try {
          setApiError('');
          setStatusMessage('Updating profile...');
          const updatePromises = [updateProfile(token, profileFormData)];
          if (addressId) {
            updatePromises.push(updateAddress(token, addressId, addressPayload));
          }
          const results = await Promise.allSettled(updatePromises);
          const errors = results.filter(r => r.status === 'rejected');
          if (errors.length > 0) throw new Error('One or more updates failed');

          const profileResp = await getProfile(token);
          const updatedUser = profileResp.data?.data || profileResp.data;
          if (profileResp.success && updatedUser) {
            setUserData(updatedUser);
            if (updatedUser.addresses && updatedUser.addresses.length > 0) {
              setAddressId(updatedUser.addresses[0]._id);
            }
            setFormData(prev => ({
              ...prev,
              name: updatedUser.name || updatedUser.user_name || '',
              email: updatedUser.email || '',
              mobile: updatedUser.mobile || '',
              profileImage: updatedUser.profile_image || prev.profileImage,
              building_number: updatedUser.addresses?.[0]?.building_number || '',
              block: updatedUser.addresses?.[0]?.block || '',
              land_mark: updatedUser.addresses?.[0]?.land_mark || '',
              city: updatedUser.addresses?.[0]?.city || '',
              pincode: updatedUser.addresses?.[0]?.pincode || ''
            }));
          }
          setProfileImageFile(null);
          setStatusMessage('Profile updated successfully!');
          setTimeout(() => setStatusMessage(''), 3000);
        } catch (err) {
          setApiError(err.message || 'Failed to update profile');
          setTimeout(() => setApiError(''), 3000);
        }
      })();
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, profileImage: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('technician_token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('technician_info');
    eventBus.emit('userLoggedOut');
    navigate('/login');
  };

  return (
    <div className="profileflowContainer">
      <div className="profileflowWrapper">
        {/* Left Side - Profile Card */}
        <div className="profileflowLeftPanel">
          <div className="profileflowCard">
            <div className="profileflowImageWrapper">
              <div className="profileflowImageContainer">
                <img
                  src={formData.profileImage || img}
                  alt="Profile"
                  className="profileflowProfileImage"
                />
                {isEditing && (
                  <label className="profileflowUploadBtn">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <span className="profileflowCameraIcon">📷</span>
                  </label>
                )}
              </div>
            </div>

            <div className="profileflowInfoSection">
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  className="profileflowEditInput profileflowNameInput"
                  placeholder="Your Name"
                />
              ) : (
                <h2 className="profileflowUserName">
                  {userData?.user_name || formData.name || 'Guest User'}
                </h2>
              )}

              {isEditing ? (
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="profileflowEditInput profileflowLocationInput"
                  placeholder="Your City"
                />
              ) : (
                <p className="profileflowUserLocation">
                  📍 {formData.city || 'Location not set'}
                </p>
              )}

              <button
                className={`profileflowEditBtn ${isEditing ? 'profileflowSaveBtn' : ''}`}
                onClick={handleEditToggle}
              >
                {isEditing ? '💾 Save Changes' : '✏️ Edit Profile'}
              </button>
              <button
                type="button"
                className="profileflowLogoutBtn"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>

            {statusMessage && <div className="profileflowStatusMsg">{statusMessage}</div>}
          </div>

          {/* Stats */}
          <div className="profileflowStats">
            <div className="profileflowStatItem">
              <h3 className="profileflowStatNumber">{totalViewed || '0'}</h3>
              <p className="profileflowStatLabel">Technicians Viewed</p>
            </div>
            <div className="profileflowStatItem">
              <h3 className="profileflowStatNumber">
                {loadingFeedbackNotifications ? '--' : submittedFeedbackCount}
              </h3>
              <p className="profileflowStatLabel">Reviews Given</p>
            </div>
          </div>
        </div>

        {/* Right Side - Details */}
        <div className="profileflowRightPanel">
          {/* Contact Information */}
          <div className="profileflowSection">
            <h3 className="profileflowSectionTitle">📞 Contact Information</h3>
            <div className="profileflowInfoGrid">
              <div className="profileflowInfoItem">
                <span className="profileflowInfoLabel">Email</span>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="profileflowEditInput"
                  />
                ) : (
                  <p className="profileflowInfoValue">{userData?.email || formData.email || 'Not set'}</p>
                )}
              </div>
              <div className="profileflowInfoItem">
                <span className="profileflowInfoLabel">Mobile</span>
                {isEditing ? (
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className="profileflowEditInput"
                  />
                ) : (
                  <p className="profileflowInfoValue">{userData?.mobile || formData.mobile || 'Not set'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="profileflowSection">
            <h3 className="profileflowSectionTitle">🏠 Address Details</h3>
            <div className="profileflowAddressGrid">
              <div className="profileflowAddressItem">
                <span className="profileflowInfoLabel">Building/Apartment</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="building_number"
                    value={formData.building_number}
                    onChange={handleInputChange}
                    className="profileflowEditInput"
                    placeholder="Building name/number"
                  />
                ) : (
                  <p className="profileflowInfoValue">{formData.building_number || 'Not set'}</p>
                )}
              </div>
              <div className="profileflowAddressItem">
                <span className="profileflowInfoLabel">Block/Sector</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="block"
                    value={formData.block}
                    onChange={handleInputChange}
                    className="profileflowEditInput"
                    placeholder="Block/Sector"
                  />
                ) : (
                  <p className="profileflowInfoValue">{formData.block || 'Not set'}</p>
                )}
              </div>
              <div className="profileflowAddressItem">
                <span className="profileflowInfoLabel">Landmark</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="land_mark"
                    value={formData.land_mark}
                    onChange={handleInputChange}
                    className="profileflowEditInput"
                    placeholder="Nearby landmark"
                  />
                ) : (
                  <p className="profileflowInfoValue">{formData.land_mark || 'Not set'}</p>
                )}
              </div>
              <div className="profileflowAddressItem">
                <span className="profileflowInfoLabel">Pincode</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    className="profileflowEditInput"
                    placeholder="6-digit pincode"
                    maxLength="6"
                  />
                ) : (
                  <p className="profileflowInfoValue">{formData.pincode || 'Not set'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Recently Viewed Technicians */}
          <div className="profileflowSection">
            <div className="profileflowSectionHeader">
              <h3 className="profileflowSectionTitle" style={{ marginBottom: 0 }}>
                👀 Recently Viewed Technicians
              </h3>
              {recentlyViewed.length > 0 && (
                <span className="profileflowBadge">{totalViewed} total</span>
              )}
            </div>
            <div className="profileflowTechList">
              {displayedTechnicians.length > 0 ? (
                displayedTechnicians.map((tech) => {
                  const categoryName = tech.category?.[0]?.categoryName || 'Service Provider';
                  const tel = tech.mobile ? `tel:${tech.mobile}` : null;
                  return (
                    <div key={tech._id || tech.technician_id} className="profileflowTechCard">
                      <img
                        src={tech.profile_pic || img}
                        alt={tech.name}
                        className="profileflowTechAvatar"
                        onError={(e) => { e.currentTarget.src = img; }}
                      />
                      <div className="profileflowTechInfo">
                        <h4 className="profileflowTechName">{tech.name || 'Technician'}</h4>
                        <p className="profileflowTechCategory">🔧 {categoryName}</p>
                        <p className="profileflowTechLocation">
                          📍 {tech.city || 'Location not set'}
                        </p>
                      </div>
                      {tel ? (
                        <a href={tel} className="profileflowCallBtn" aria-label={`Call ${tech.name}`}>
                          📞 Call Now
                        </a>
                      ) : (
                        <span className="profileflowCallBtn profileflowCallBtnDisabled">📞 No Phone</span>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="profileflowEmptyMessage">No recently viewed technicians yet.</p>
              )}
            </div>
            {recentlyViewed.length > 3 && (
              <button
                type="button"
                className="profileflowShowMoreBtn"
                onClick={() => setShowAllTechnicians(v => !v)}
              >
                {showAllTechnicians
                  ? 'Show Less'
                  : `Show All (${recentlyViewed.length})`}
              </button>
            )}
          </div>

          {/* Feedback Requests (pending unique pairs by default) */}
          <div className="profileflowSection">
            <div className="profileflowSectionHeader">
              <h3 className="profileflowSectionTitle" style={{ marginBottom: 0 }}>
                ⭐ Feedback Requests
              </h3>
              {pendingNotifications.length > 0 && (
                <span className="profileflowBadge">{pendingNotifications.length} pending</span>
              )}
            </div>

            <div className="profileflowReviewsList">
              {loadingFeedbackNotifications ? (
                <p className="profileflowEmptyMessage">Loading feedback requests...</p>
              ) : feedbackNotificationsError ? (
                <p className="profileflowEmptyMessage profileflowErrorMsg">{feedbackNotificationsError}</p>
              ) : displayedNotifications.length > 0 ? (
                displayedNotifications.map((notification) => {
                  const techName =
                    notification.technician?.name ||
                    notification.metadata?.technicianName ||
                    'Technician';
                  const techPic = notification.technician?.profile_pic || img;
                  return (
                    <div
                      key={`${notification.userId}-${notification.metadata?.technicianId}-${notification._id}`}
                      className="profileflowReviewCard"
                    >
                      <div className="profileflowReviewHeader">
                        <div className="profileflowReviewerInfo">
                          <img
                            src={techPic}
                            alt={techName}
                            className="profileflowReviewerAvatarImg"
                            onError={(e) => { e.currentTarget.src = img; }}
                          />
                          <div>
                            <h4 className="profileflowReviewerName">{techName}</h4>
                            <p className="profileflowReviewService">
                              {notification.message || 'Please provide feedback for your recent service.'}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="profileflowReviewFooter">
                        <span className="profileflowReviewDate">
                          {notification.createdAt
                            ? new Date(notification.createdAt).toLocaleDateString()
                            : ''}
                        </span>
                        <button
                          type="button"
                          className="profileflowRateBtn"
                          onClick={() =>
                            openReviewModal?.({
                              notification,
                              notificationId: notification._id,
                              technicianId:
                                notification.metadata?.technicianId ||
                                notification.technician?.technician_id,
                              viewLogId: notification.metadata?.viewId,
                              technicianName: techName,
                            })
                          }
                        >
                          Rate now
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="profileflowEmptyMessage">
                  You're all caught up — no pending reviews.
                </p>
              )}
            </div>

            {pendingNotifications.length > 3 && (
              <button
                type="button"
                className="profileflowShowMoreBtn"
                onClick={() => setShowAllFeedback(v => !v)}
              >
                {showAllFeedback
                  ? 'Show Less'
                  : `Show All (${pendingNotifications.length})`}
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .profileflowContainer {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
        }
        .profileflowWrapper {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          gap: 32px;
          animation: profileflowFadeIn 0.5s ease-out;
        }
        @keyframes profileflowFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Left Panel */
        .profileflowLeftPanel {
          flex: 0.8;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .profileflowCard {
          background: #FFFFFF;
          border-radius: 28px;
          padding: 32px 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          border: 1px solid #f0f0f0;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .profileflowCard:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
        }
        .profileflowImageWrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 24px;
        }
        .profileflowImageContainer { position: relative; display: inline-block; }
        .profileflowProfileImage {
          width: 150px; height: 150px;
          border-radius: 75px; object-fit: cover;
          border: 4px solid #FFFFFF;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s ease;
        }
        .profileflowProfileImage:hover { transform: scale(1.02); }
        .profileflowUploadBtn {
          position: absolute; bottom: 5px; right: 5px;
          background: #000000; border-radius: 40px; padding: 10px;
          cursor: pointer; transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }
        .profileflowUploadBtn:hover { transform: scale(1.1); background: #333; }
        .profileflowCameraIcon { font-size: 1.2rem; }
        .profileflowInfoSection { text-align: center; }
        .profileflowUserName { font-size: 1.5rem; font-weight: 700; color: #000; margin-bottom: 8px; }
        .profileflowUserLocation { color: #666; font-size: 0.9rem; margin-bottom: 20px; }
        .profileflowEditBtn {
          width: 100%; background: #000000; color: #FFFFFF; border: none;
          padding: 12px 24px; border-radius: 40px; font-size: 0.95rem;
          font-weight: 600; cursor: pointer; transition: all 0.3s ease;
        }
        .profileflowEditBtn:hover {
          background: #222; transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }
        .profileflowLogoutBtn {
          width: 100%; margin-top: 12px; padding: 12px 24px;
          border: 1px solid #d93025; border-radius: 40px; background: #fff;
          color: #d93025; font-size: 0.95rem; font-weight: 600;
          cursor: pointer; transition: all 0.3s ease;
        }
        .profileflowLogoutBtn:hover { background: #d93025; color: #fff; }
        .profileflowSaveBtn { background: #28a745; }
        .profileflowSaveBtn:hover { background: #218838; }

        /* Stats */
        .profileflowStats {
          background: #FFFFFF; border-radius: 28px; padding: 24px;
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;
          text-align: center; border: 1px solid #f0f0f0;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
        }
        .profileflowStatItem { padding: 8px; }
        .profileflowStatNumber { font-size: 1.75rem; font-weight: 700; color: #000; margin-bottom: 6px; }
        .profileflowStatLabel {
          color: #888; font-size: 0.8rem; text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Right Panel */
        .profileflowRightPanel { flex: 1.2; display: flex; flex-direction: column; gap: 24px; }
        .profileflowSection {
          background: #FFFFFF; border-radius: 28px; padding: 28px 32px;
          border: 1px solid #f0f0f0; transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
        }
        .profileflowSection:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        }
        .profileflowSectionHeader {
          display: flex; justify-content: space-between;
          align-items: center; margin-bottom: 16px;
        }
        .profileflowSectionTitle { font-size: 1.3rem; font-weight: 600; color: #000; margin-bottom: 16px; }
        .profileflowBadge {
          background: #000; color: #fff; font-size: 0.75rem; font-weight: 600;
          padding: 4px 10px; border-radius: 20px;
        }

        /* Show More button */
        .profileflowShowMoreBtn {
          display: block; width: 100%; margin-top: 16px;
          padding: 10px 16px; border: 1px solid #e0e0e0;
          border-radius: 24px; background: #fff; color: #000;
          font-size: 0.85rem; font-weight: 600; cursor: pointer;
          transition: all 0.3s ease;
        }
        .profileflowShowMoreBtn:hover {
          background: #f5f5f5; border-color: #000;
          transform: translateY(-1px);
        }

        /* Form */
        .profileflowEditInput {
          width: 100%; padding: 10px 14px; font-size: 0.95rem;
          border: 2px solid #e8e8e8; border-radius: 12px;
          transition: all 0.3s ease; outline: none; font-family: inherit;
        }
        .profileflowEditInput:focus { border-color: #000; box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05); }
        .profileflowNameInput { font-size: 1.2rem; font-weight: 600; text-align: center; }
        .profileflowLocationInput { text-align: center; margin-bottom: 16px; }

        /* Info Grid */
        .profileflowInfoGrid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
        }
        .profileflowInfoItem { display: flex; flex-direction: column; gap: 8px; }
        .profileflowAddressGrid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }
        .profileflowAddressItem { display: flex; flex-direction: column; gap: 8px; }
        .profileflowInfoLabel {
          font-size: 0.8rem; font-weight: 600; color: #888;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .profileflowInfoValue { color: #333; font-weight: 500; }

        /* Technicians */
        .profileflowTechList { display: flex; flex-direction: column; gap: 16px; }
        .profileflowTechCard {
          display: flex; align-items: center; gap: 16px;
          padding: 16px; border: 1px solid #f0f0f0; border-radius: 20px;
          transition: all 0.3s ease; background: #fff;
        }
        .profileflowTechCard:hover {
          border-color: #000; transform: translateX(4px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
        }
        .profileflowTechAvatar {
          width: 56px; height: 56px; border-radius: 50%;
          object-fit: cover; flex-shrink: 0;
          border: 2px solid #f0f0f0;
        }
        .profileflowTechInfo { flex: 1; min-width: 0; }
        .profileflowTechName {
          font-size: 1rem; font-weight: 600; color: #000;
          margin-bottom: 4px; overflow: hidden;
          text-overflow: ellipsis; white-space: nowrap;
        }
        .profileflowTechCategory { font-size: 0.8rem; color: #666; margin-bottom: 2px; }
        .profileflowTechLocation { font-size: 0.75rem; color: #999; }
        .profileflowCallBtn {
          flex-shrink: 0; display: inline-block;
          background: linear-gradient(135deg, #000, #333);
          color: #fff; text-decoration: none;
          padding: 10px 16px; border-radius: 24px;
          font-size: 0.85rem; font-weight: 600;
          transition: all 0.3s ease; white-space: nowrap;
        }
        .profileflowCallBtn:hover {
          background: linear-gradient(135deg, #28a745, #1e7e34);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
        }
        .profileflowCallBtnDisabled {
          background: #ccc; cursor: not-allowed; pointer-events: none;
        }

        /* Reviews */
        .profileflowReviewsList { display: flex; flex-direction: column; gap: 16px; }
        .profileflowReviewCard {
          padding: 20px; border: 1px solid #f0f0f0;
          border-radius: 20px; transition: all 0.3s ease;
        }
        .profileflowReviewCard:hover {
          border-color: #000; transform: translateX(4px);
        }
        .profileflowReviewHeader {
          display: flex; justify-content: space-between;
          align-items: flex-start; margin-bottom: 12px;
        }
        .profileflowReviewerInfo { display: flex; gap: 12px; align-items: center; }
        .profileflowReviewerAvatarImg {
          width: 44px; height: 44px; border-radius: 50%;
          object-fit: cover; flex-shrink: 0;
          border: 2px solid #f0f0f0;
        }
        .profileflowReviewerName { font-weight: 600; color: #000; margin-bottom: 4px; }
        .profileflowReviewService { font-size: 0.8rem; color: #888; }
        .profileflowReviewFooter {
          display: flex; justify-content: space-between; align-items: center;
        }
        .profileflowReviewDate { font-size: 0.7rem; color: #999; }
        .profileflowRateBtn {
          background: #000; color: #fff; border: none;
          padding: 8px 18px; border-radius: 24px;
          font-size: 0.85rem; font-weight: 600;
          cursor: pointer; transition: all 0.3s ease;
        }
        .profileflowRateBtn:hover {
          background: #222; transform: translateY(-1px);
        }

        /* Empty / status */
        .profileflowEmptyMessage { color: #666; font-size: 0.95rem; margin: 0; padding: 12px 0; }
        .profileflowBookingStatus { font-size: 0.75rem; color: #28a745; font-weight: 600; }

        /* Messages */
        .profileflowStatusMsg {
          margin-top: 16px; padding: 10px; background: #d4edda;
          border-left: 4px solid #28a745; color: #155724;
          border-radius: 8px; font-size: 0.85rem;
          animation: profileflowSlideIn 0.3s ease;
        }
        .profileflowErrorMsg {
          margin-top: 16px; padding: 10px; background: #f8d7da;
          border-left: 4px solid #dc3545; color: #721c24;
          border-radius: 8px; font-size: 0.85rem;
        }
        @keyframes profileflowSlideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Responsive */
        @media (max-width: 968px) {
          .profileflowWrapper { flex-direction: column; gap: 24px; }
          .profileflowLeftPanel, .profileflowRightPanel { flex: auto; }
          .profileflowProfileImage { width: 120px; height: 120px; }
        }
        @media (max-width: 768px) {
          .profileflowStats { gap: 12px; }
          .profileflowStatNumber { font-size: 1.25rem; }
          .profileflowSection { padding: 20px; }
          .profileflowInfoGrid, .profileflowAddressGrid { grid-template-columns: 1fr; gap: 16px; }
          .profileflowTechCard { flex-wrap: wrap; }
          .profileflowTechInfo { flex-basis: 100%; }
          .profileflowReviewerInfo { flex-basis: 100%; }
        }
        @media (max-width: 480px) {
          .profileflowContainer { padding: 12px; }
          .profileflowStatNumber { font-size: 1.1rem; }
          .profileflowStatLabel { font-size: 0.7rem; }
          .profileflowReviewHeader { flex-direction: column; gap: 12px; }
          .profileflowReviewFooter { flex-direction: column; gap: 8px; align-items: flex-start; }
          .profileflowCallBtn { width: 100%; text-align: center; }
          .profileflowReviewerAvatarImg { width: 36px; height: 36px; }
          .profileflowShowMoreBtn { font-size: 0.8rem; padding: 9px 14px; }
        }
      `}</style>
    </div>
  );
};

export default UserProfile;